import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, ButtonType, Modal, ModalSize } from "@renta-apps/athenaeum-react-components";
import { NewsItem } from "@/models/server/NewsItem";
import { acknowledgeNewsAsync } from "@/providers/NewsProvider";
import NewsCarouselItem from "@/components/NewsCarousel/NewsCarouselItem";
import Localizer from "@/localization/Localizer";

import styles from "./PriorityNewsModal.module.scss";

interface IPriorityNewsModalProps {
    newsItems: NewsItem[];
}

/**
 * Blocking modal that forces users to read and acknowledge priority news items.
 * Items are shown one at a time; each must be acknowledged (persisted per-user) before
 * the next appears, and the modal cannot be dismissed until all are acknowledged.
 *
 * The PRIORITY_NEWS feature flag is enforced server-side (items come back with priority=false
 * when the flag is off), so this component opens purely from data and does not depend on
 * client-side feature-flag initialization timing.
 */
const PriorityNewsModal: React.FC<IPriorityNewsModalProps> = ({ newsItems = [] }) => {
    const modalRef = useRef<Modal>(null);
    const [queue, setQueue] = useState<NewsItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [processing, setProcessing] = useState<boolean>(false);

    const unacknowledgedPriority: NewsItem[] = useMemo(
        () => newsItems.filter(item => item.priority && !item.acknowledged && !!item.id),
        [newsItems]
    );

    useEffect(() => {
        if (unacknowledgedPriority.length > 0) {
            setQueue(unacknowledgedPriority);
            setCurrentIndex(0);
            modalRef.current?.openAsync();
        }
    }, [unacknowledgedPriority]);

    const currentItem: NewsItem | null = queue[currentIndex] || null;

    const acknowledgeCurrentAsync = async (): Promise<void> => {
        if (!currentItem || !currentItem.id || processing) {
            return;
        }

        setProcessing(true);

        try {
            await acknowledgeNewsAsync(currentItem.id);

            const nextIndex: number = currentIndex + 1;
            if (nextIndex < queue.length) {
                setCurrentIndex(nextIndex);
            } else {
                await modalRef.current?.closeAsync();
            }
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Modal noHeader
               id="priorityNewsModal"
               ref={modalRef}
               className={styles.priorityNewsModal}
               size={ModalSize.Large}
               preventEsc
               preventClosingOnOutsideClick
        >
            <div className={styles.header}>
                <h5>{Localizer.priorityNewsModalTitle}</h5>
            </div>

            {
                (currentItem) &&
                (
                    <div className={styles.content}>
                        <NewsCarouselItem newsItem={currentItem} index={currentIndex} />
                    </div>
                )
            }

            <div className={styles.actions}>
                <Button block
                        type={ButtonType.Orange}
                        label={Localizer.priorityNewsModalAcknowledge}
                        disabled={processing}
                        onClick={() => acknowledgeCurrentAsync()}
                        id="priorityNewsAcknowledgeButton"
                />
            </div>
        </Modal>
    );
};

export default PriorityNewsModal;
